import React, { useState, useEffect } from "react";
import DateNavigator from "../global/dateNavigator";
import api from "services/api";
import { FaSave, FaCamera, FaEdit, FaTrash, FaTimes } from "react-icons/fa";
import toast from "react-hot-toast";
import useStore from "services/store";

export const TrackProgress = () => {
  const { user, setUser } = useStore();
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [measurements, setMeasurements] = useState({
    // Weight
    weight: "",

    // Body fat  
    body_fat: "",

    // Arms
    left_arm: "",
    right_arm: "",
    left_forearm: "",
    right_forearm: "",

    // Torso
    neck: "",
    torso: "",
    waist: "",
    hips: "",

    // Legs
    left_thigh: "",
    right_thigh: "",
    left_calf: "",
    right_calf: "",

    // Notes
    notes: "",
  });
  const [pictures, setPictures] = useState({
    front_picture: null,
    back_picture: null,
    side_picture: null,
  });
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [hasExistingEntry, setHasExistingEntry] = useState(false);

  useEffect(() => {
    fetchProgress();
  }, [date]);

  const fetchProgress = async () => {
    const response = await api.get(`/progress/${date}`);
    if (!response.ok) {
      setMeasurements(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.keys(prev).map(key => [key, ""])
        )
      }));
      setPictures(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.keys(prev).map(key => [key, null])
        )
      }));
      setHasExistingEntry(false);
      setIsEditing(false);
      return;
    }
  
    setMeasurements(prev => ({
      ...prev,
      ...Object.fromEntries(
        Object.keys(prev).map(key => [key, response.data[key] || ""])
      )
    }));
    setHasExistingEntry(true);
    setIsEditing(false);
  };

  const handleDateChange = (newDate) => {
    setDate(newDate);
  };

  const handleMeasurementChange = (measurement, value) => {
    setMeasurements((prev) => ({
      ...prev,
      [measurement]: value,
    }));
  };

  const handlePictureChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setPictures((prev) => ({
        ...prev,
        [type]: file,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!measurements.weight || measurements.weight.toString().trim() === '') {
      newErrors.weight = "Weight is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchProgress();
  };

  const handleDelete = async () => {
    try {
      const confirm = window.confirm("Are you sure you want to delete this progress?");
      if (!confirm) {
        return;
      }
      const response = await api.delete(`/progress/${date}`);
      if (!response.ok) {
        toast.error("Failed to delete progress");
        return;
      }
      toast.success("Progress deleted successfully!");
      fetchProgress();
    } catch (error) {
      console.error("Error deleting progress:", error);
      toast.error("Failed to delete progress");
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Fill in all required fields");
      return;
    }

    const filteredMeasurements = Object.fromEntries(
      Object.entries(measurements).filter(([_, value]) => value !== "")
    );

    const requestBody = {
      date,
      ...filteredMeasurements,
      ...pictures,
    };

    try {
      const response = await api.post('/progress', requestBody);
      if (!response.ok) {
        toast.error('Failed to save progress');
        return;
      }
      toast.success('Progress saved successfully!');
      setUser(response.user);
      fetchProgress();
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress');
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      toast.error("Fill in all required fields");
      return;
    }

    const filteredMeasurements = Object.fromEntries(
      Object.entries(measurements).filter(([_, value]) => value !== "")
    );

    const requestBody = {
      date,
      ...filteredMeasurements,
      ...pictures,
    };

    try {
      const response = await api.put(`/progress/${date}`, requestBody);
      if (!response.ok) {
        toast.error('Failed to update progress');
        return;
      }
      toast.success('Progress updated successfully!');
      setIsEditing(false);
      fetchProgress();
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      handleUpdate();
    } else {
      handleSave();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl mx-auto p-4">
      {/* Date selection */}
      <div className="flex justify-between items-center">
        <DateNavigator selectedDate={date} onDateChange={handleDateChange} />
      </div>

      {/* Required weight */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Required</h3>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="flex items-center mb-2 justify-between">
            <label className="text-gray-700 font-medium">
              Weight <span className="text-red-500">*</span>
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="number"
              value={measurements.weight}
              onChange={(e) =>
                handleMeasurementChange("weight", e.target.value)
              }
              className={`flex-1 p-2 border ${
                errors.weight ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none`}
              placeholder="Enter a value"
              disabled={hasExistingEntry && !isEditing}
            />
            <span className="ml-2 text-gray-600">kg</span>
          </div>
          {errors.weight && (
            <p className="mt-1 text-red-500 text-sm">{errors.weight}</p>
          )}
        </div>
      </div>

      {/* Torso */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Torso</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Neck</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.neck}
                onChange={(e) =>
                  handleMeasurementChange("neck", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Chest</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.torso}
                onChange={(e) =>
                  handleMeasurementChange("torso", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Waist</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.waist}
                onChange={(e) =>
                  handleMeasurementChange("waist", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Hips</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.hips}
                onChange={(e) =>
                  handleMeasurementChange("hips", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>
        </div>
      </div>

      {/* Arms */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Arms</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Left Arm</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.left_arm}
                onChange={(e) =>
                  handleMeasurementChange("left_arm", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Right Arm</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.right_arm}
                onChange={(e) =>
                  handleMeasurementChange("right_arm", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Left Forearm</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.left_forearm}
                onChange={(e) =>
                  handleMeasurementChange("left_forearm", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Right Forearm</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.right_forearm}
                onChange={(e) =>
                  handleMeasurementChange("right_forearm", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>
        </div>
      </div>

      {/* Legs */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Legs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Left Thigh</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.left_thigh}
                onChange={(e) =>
                  handleMeasurementChange("left_thigh", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Right Thigh</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.right_thigh}
                onChange={(e) =>
                  handleMeasurementChange("right_thigh", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Left Calf</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.left_calf}
                onChange={(e) =>
                  handleMeasurementChange("left_calf", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <div className="flex items-center mb-2 justify-between">
              <label className="text-gray-700 font-medium">Right Calf</label>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                value={measurements.right_calf}
                onChange={(e) =>
                  handleMeasurementChange("right_calf", e.target.value)
                }
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
                placeholder="Enter a value"
                disabled={hasExistingEntry && !isEditing}
              />
              <span className="ml-2 text-gray-600">cm</span>
            </div>
          </div>
        </div>
      </div>

      {/* Photos */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Progress Photos
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(pictures).map(([key, file]) => (
            <div key={key} className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handlePictureChange(e, key)}
                className="hidden"
                id={key}
                disabled={hasExistingEntry && !isEditing}
              />
              <label
                htmlFor={key}
                className={`block w-full aspect-square border-2 border-dashed border-gray-300 rounded-lg ${
                  hasExistingEntry && !isEditing ? 'cursor-not-allowed' : 'hover:border-blue-500 cursor-pointer'
                } transition-colors`}
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                  {file ? (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={key}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <>
                      <FaCamera className="text-3xl text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500 text-center">
                        {key === "front_picture" ? "Front Photo" :
                         key === "back_picture" ? "Back Photo" :
                         "Side Photo"}
                      </span>
                    </>
                  )}
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Notes</h3>
        <textarea
          value={measurements.notes}
          onChange={(e) => handleMeasurementChange("notes", e.target.value)}
          className="w-full p-4 border border-gray-300 rounded-lg min-h-[100px] focus:ring-2 focus:ring-black focus:border-black outline-none"
          placeholder="Add notes about your progress..."
          disabled={hasExistingEntry && !isEditing}
        />
      </div>

      {/* Action buttons */}
      <div className="flex justify-end pt-6 space-x-4">
        {hasExistingEntry && !isEditing && (
          <>
            <button
              type="button"
              onClick={handleEdit}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaEdit className="mr-2" />
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="flex items-center px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FaTrash className="mr-2" />
              Delete
            </button>
          </>
        )}
        {isEditing && (
          <>
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FaTimes className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaSave className="mr-2" />
              Update
            </button>
          </>
        )}
        {!hasExistingEntry && (
          <button
            type="submit"
            className="flex items-center px-6 py-2 bg-black text-white rounded-lg hover:bg-black-700 transition-colors"
          >
            <FaSave className="mr-2" />
            Save Progress
          </button>
        )}
      </div>
    </form>
  );
};
